import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  collection,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  query,
  where,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { getDb } from '../firebase';

const migratePlaceholderOverrides = async (familyId, placeholderId, realUserId) => {
  const db = getDb();
  const overridesRef = collection(db, 'families', familyId, 'custodyOverrides');
  const snapshot = await getDocs(overridesRef);

  if (snapshot.empty) return;

  const batch = writeBatch(db);
  let hasUpdates = false;

  snapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    const updates = {};

    if (data.originalParentId === placeholderId) updates.originalParentId = realUserId;
    if (data.newParentId === placeholderId) updates.newParentId = realUserId;
    if (data.proposedBy === placeholderId) updates.proposedBy = realUserId;
    if (data.respondedBy === placeholderId) updates.respondedBy = realUserId;

    if (Object.keys(updates).length > 0) {
      batch.update(docSnap.ref, updates);
      hasUpdates = true;
    }
  });

  if (hasUpdates) {
    await batch.commit();
  }
};

export const createFamily = async (userId, familyName, userDisplayName) => {
  const db = getDb();
  const familyRef = doc(collection(db, 'families'));
  const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

  const familyData = {
    name: familyName,
    parentIds: [userId],
    parentProfiles: {
      [userId]: {
        displayName: userDisplayName,
        color: '#3b82f6',
        role: 'parent'
      }
    },
    childrenIds: [],
    createdBy: userId,
    createdAt: Timestamp.now(),
    inviteCode,
    inviteEmail: null,
    inviteStatus: 'pending',
    settings: {
      defaultSplitRatio: 50,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      weekStartsOn: 0
    }
  };

  // Write the family, the creator's familyIds, and the backing invite doc
  // atomically. If these could diverge (e.g. the invite write failing while the
  // family write lands), the family would advertise an `inviteCode` with no
  // `invites/{code}` doc behind it — so the co-parent's join would fail with
  // "Invalid invite code." The batch keeps the displayed code and its invite
  // doc consistent.
  const batch = writeBatch(db);
  batch.set(familyRef, familyData);
  batch.update(doc(db, 'users', userId), { familyIds: arrayUnion(familyRef.id) });
  batch.set(doc(db, 'invites', inviteCode), {
    familyId: familyRef.id,
    invitedBy: userId,
    invitedEmail: null,
    status: 'pending',
    type: 'coparent',
    createdAt: Timestamp.now(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  });
  await batch.commit();

  return { id: familyRef.id, ...familyData };
};

export const joinFamily = async (userId, userDisplayName, inviteCode) => {
  const db = getDb();
  const inviteDoc = await getDoc(doc(db, 'invites', inviteCode.toUpperCase()));
  if (!inviteDoc.exists()) {
    throw new Error('Invalid invite code');
  }

  const invite = inviteDoc.data();
  if (invite.status === 'accepted') {
    throw new Error('This invite has already been used');
  }

  const familyId = invite.familyId;
  const familyDoc = await getDoc(doc(db, 'families', familyId));
  if (!familyDoc.exists()) {
    throw new Error('Family not found');
  }

  const family = familyDoc.data();

  if (family.parentIds.includes(userId)) {
    throw new Error('You are already a member of this family');
  }

  const placeholderId = family.parentIds.find(
    pid => family.parentProfiles[pid]?.placeholder
  );

  if (family.parentIds.length >= 2 && !placeholderId) {
    await joinFamilyAsHouseholdMember(userId, userDisplayName, inviteCode, invite.invitedBy);
    return { familyId, joinedAs: 'household_member' };
  }

  const familyUpdate = {
    parentIds: arrayUnion(userId),
    [`parentProfiles.${userId}`]: {
      displayName: userDisplayName,
      color: placeholderId ? (family.parentProfiles[placeholderId]?.color || '#ec4899') : '#ec4899',
      role: 'parent'
    },
    inviteStatus: 'accepted'
  };

  if (placeholderId) {
    const newParentIds = family.parentIds
      .filter(id => id !== placeholderId)
      .concat(userId);
    familyUpdate.parentIds = newParentIds;

    const newProfiles = { ...family.parentProfiles };
    delete newProfiles[placeholderId];
    newProfiles[userId] = familyUpdate[`parentProfiles.${userId}`];
    familyUpdate.parentProfiles = newProfiles;
    delete familyUpdate[`parentProfiles.${userId}`];

    if (family.settings) {
      const newSettings = { ...family.settings };
      if (newSettings.parentLabels?.[placeholderId]) {
        newSettings.parentLabels = { ...newSettings.parentLabels };
        newSettings.parentLabels[userId] = newSettings.parentLabels[placeholderId];
        delete newSettings.parentLabels[placeholderId];
      }
      familyUpdate.settings = newSettings;
    }
  }

  // Commit the membership, the user's familyIds, and the invite acceptance
  // atomically. A batch is all-or-nothing, so a transient failure can never
  // mark the invite 'accepted' while leaving the user out of the family —
  // which previously turned a one-off hiccup into a permanent "This invite has
  // already been used" lockout.
  const batch = writeBatch(db);
  batch.update(doc(db, 'families', familyId), familyUpdate);
  batch.update(doc(db, 'users', userId), { familyIds: arrayUnion(familyId) });
  batch.update(doc(db, 'invites', inviteCode.toUpperCase()), { status: 'accepted' });
  await batch.commit();

  if (placeholderId) {
    await migratePlaceholderOverrides(familyId, placeholderId, userId);
  }

  return { familyId, joinedAs: 'coparent' };
};

export const getFamily = async (familyId) => {
  const db = getDb();
  const familyDoc = await getDoc(doc(db, 'families', familyId));
  if (familyDoc.exists()) {
    return { id: familyDoc.id, ...familyDoc.data() };
  }
  return null;
};

export const subscribeToFamily = (familyId, callback) => {
  const db = getDb();
  return onSnapshot(doc(db, 'families', familyId), (doc) => {
    if (doc.exists()) {
      callback({ id: doc.id, ...doc.data() });
    }
  }, (error) => {
    console.error('Family listener error:', error);
  });
};

export const updateFamilySettings = async (familyId, newSettings) => {
  const db = getDb();
  const familyDoc = await getDoc(doc(db, 'families', familyId));
  const current = familyDoc.exists() ? familyDoc.data().settings || {} : {};
  await updateDoc(doc(db, 'families', familyId), {
    settings: { ...current, ...newSettings }
  });
};

export const updateParentProfileField = async (familyId, parentId, field, value) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId), {
    [`parentProfiles.${parentId}.${field}`]: value
  });
};

export const addPlaceholderParent = async (familyId, displayName) => {
  const db = getDb();
  const placeholderId = `placeholder_${Math.random().toString(36).substring(2, 10)}`;

  await updateDoc(doc(db, 'families', familyId), {
    parentIds: arrayUnion(placeholderId),
    [`parentProfiles.${placeholderId}`]: {
      displayName,
      color: '#ec4899',
      role: 'parent',
      placeholder: true
    }
  });

  return placeholderId;
};

/**
 * Remove a user from a family on account deletion.
 *
 * Strategy:
 *  - If the user is a parent and the only parent → delete the family doc and
 *    its subcollections so no orphaned data is left behind.
 *  - If the user is a parent with a co-parent → convert the user's profile to
 *    a placeholder entry so the co-parent keeps their shared history
 *    (schedules, expenses, activities, etc.) intact. The user is removed from
 *    `parentIds`, their profile is dropped, and a new placeholder with the
 *    same display name is added in their slot.
 *  - If the user is a household member → remove them via
 *    `removeHouseholdMember` (which already handles the cleanup).
 *
 * Returns an object summarizing what was done, intended for logs/tests.
 */
export const detachUserFromFamily = async (familyId, userId) => {
  const db = getDb();
  const familyRef = doc(db, 'families', familyId);
  const familyDoc = await getDoc(familyRef);
  if (!familyDoc.exists()) {
    return { familyId, action: 'family_missing' };
  }

  const family = familyDoc.data();

  // Household member path
  if (family.householdMembers?.[userId]) {
    await removeHouseholdMember(familyId, userId);
    return { familyId, action: 'household_member_removed' };
  }

  const isParent = Array.isArray(family.parentIds) && family.parentIds.includes(userId);
  if (!isParent) {
    return { familyId, action: 'not_member' };
  }

  const realParentIds = (family.parentIds || []).filter(
    (pid) => pid !== userId && !family.parentProfiles?.[pid]?.placeholder
  );

  // Sole real parent → delete family and its subcollections
  if (realParentIds.length === 0) {
    await deleteFamilyAndSubcollections(familyId, family);
    return { familyId, action: 'family_deleted' };
  }

  // Co-parent exists → swap the user for a placeholder of the same identity
  const existingProfile = family.parentProfiles?.[userId] || {};
  const placeholderId = `placeholder_${Math.random().toString(36).substring(2, 10)}`;
  const placeholderProfile = {
    displayName: existingProfile.displayName || 'Former co-parent',
    color: existingProfile.color || '#ec4899',
    role: 'parent',
    placeholder: true,
  };

  const newParentIds = (family.parentIds || []).map((pid) =>
    pid === userId ? placeholderId : pid
  );
  const newProfiles = { ...(family.parentProfiles || {}) };
  delete newProfiles[userId];
  newProfiles[placeholderId] = placeholderProfile;

  const familyUpdate = {
    parentIds: newParentIds,
    parentProfiles: newProfiles,
  };

  if (family.settings) {
    const newSettings = { ...family.settings };
    if (newSettings.parentLabels?.[userId]) {
      newSettings.parentLabels = { ...newSettings.parentLabels };
      newSettings.parentLabels[placeholderId] = newSettings.parentLabels[userId];
      delete newSettings.parentLabels[userId];
    }
    familyUpdate.settings = newSettings;
  }

  await updateDoc(familyRef, familyUpdate);

  // Re-point custody overrides that referenced the deleted user to the
  // placeholder so historical swaps/entries remain readable.
  await migrateUserReferencesInOverrides(familyId, userId, placeholderId);

  return { familyId, action: 'converted_to_placeholder', placeholderId };
};

/**
 * Delete all docs in a Firestore query snapshot, chunked to fit within the
 * 500-write batch limit.
 */
const batchedDelete = async (db, docs) => {
  if (!docs || !docs.length) return;
  for (let i = 0; i < docs.length; i += 400) {
    const batch = writeBatch(db);
    docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
};

/**
 * Delete a family document and every known subcollection. Called when the
 * departing user is the last real parent.
 *
 * Firestore does NOT cascade-delete subcollections on parent-doc delete, so
 * this function walks each known collection explicitly. Collections that
 * themselves contain nested subcollections (threads → messages,
 * chores → completions) are handled recursively before the parent doc is
 * removed.
 */
const deleteFamilyAndSubcollections = async (familyId, family) => {
  const db = getDb();
  const familyPath = ['families', familyId];

  // Flat subcollections (no nested children). Order doesn't matter.
  const flatSubcollections = [
    'custodySchedules',
    'custodyOverrides',
    'expenses',
    'payments',
    'activities',
    'messages',
    'documents',
    'swaps',
    'children',
    'notifications',
    'recurringExpenses',
    'householdRules',
  ];

  for (const sub of flatSubcollections) {
    try {
      const snap = await getDocs(collection(db, ...familyPath, sub));
      await batchedDelete(db, snap.docs);
    } catch (err) {
      console.warn(`Failed to delete subcollection ${sub}`, err);
    }
  }

  // Nested: threads → messages. Delete each thread's messages, then the thread.
  try {
    const threadsSnap = await getDocs(collection(db, ...familyPath, 'threads'));
    for (const threadDoc of threadsSnap.docs) {
      const msgsSnap = await getDocs(
        collection(db, ...familyPath, 'threads', threadDoc.id, 'messages')
      );
      await batchedDelete(db, msgsSnap.docs);
    }
    await batchedDelete(db, threadsSnap.docs);
  } catch (err) {
    console.warn('Failed to delete threads', err);
  }

  // Nested: chores → completions.
  try {
    const choresSnap = await getDocs(collection(db, ...familyPath, 'chores'));
    for (const choreDoc of choresSnap.docs) {
      const compsSnap = await getDocs(
        collection(db, ...familyPath, 'chores', choreDoc.id, 'completions')
      );
      await batchedDelete(db, compsSnap.docs);
    }
    await batchedDelete(db, choresSnap.docs);
  } catch (err) {
    console.warn('Failed to delete chores', err);
  }

  // Clean up any invites pointing at this family.
  try {
    const invitesSnap = await getDocs(
      query(collection(db, 'invites'), where('familyId', '==', familyId))
    );
    await batchedDelete(db, invitesSnap.docs);
  } catch (err) {
    console.warn('Failed to clean up invites for family', familyId, err);
  }

  await deleteDoc(doc(db, 'families', familyId));
};

/**
 * Re-point any custodyOverride fields from `fromUserId` to `toUserId` so the
 * co-parent's history stays readable after the user converts to a placeholder.
 */
const migrateUserReferencesInOverrides = async (familyId, fromUserId, toUserId) => {
  const db = getDb();
  const snap = await getDocs(collection(db, 'families', familyId, 'custodyOverrides'));
  if (snap.empty) return;

  const batch = writeBatch(db);
  let hasUpdates = false;
  snap.docs.forEach((docSnap) => {
    const data = docSnap.data();
    const updates = {};
    if (data.originalParentId === fromUserId) updates.originalParentId = toUserId;
    if (data.newParentId === fromUserId) updates.newParentId = toUserId;
    if (data.proposedBy === fromUserId) updates.proposedBy = toUserId;
    if (data.respondedBy === fromUserId) updates.respondedBy = toUserId;
    if (Object.keys(updates).length > 0) {
      batch.update(docSnap.ref, updates);
      hasUpdates = true;
    }
  });

  if (hasUpdates) await batch.commit();
};

export const removePlaceholderParent = async (familyId, placeholderId) => {
  const db = getDb();
  const familyDoc = await getDoc(doc(db, 'families', familyId));
  if (!familyDoc.exists()) throw new Error('Family not found');

  const family = familyDoc.data();
  const profile = family.parentProfiles[placeholderId];
  if (!profile?.placeholder) throw new Error('Can only remove placeholder parents');

  const newParentIds = family.parentIds.filter(id => id !== placeholderId);
  const newProfiles = { ...family.parentProfiles };
  delete newProfiles[placeholderId];

  const newSettings = { ...family.settings };
  if (newSettings.parentLabels) {
    const newLabels = { ...newSettings.parentLabels };
    delete newLabels[placeholderId];
    newSettings.parentLabels = newLabels;
  }

  await updateDoc(doc(db, 'families', familyId), {
    parentIds: newParentIds,
    parentProfiles: newProfiles,
    settings: newSettings
  });
};

/**
 * Request delivery of the co-parent invite email.
 *
 * Writing `invites/{code}.invitedEmail` is the request — the
 * `onInviteEmailRequested` Cloud Function (functions/invites/sendInvite.js)
 * watches that field and does the actual send via Resend. The client
 * deliberately doesn't call the mail provider itself: the API key stays
 * server-side, and the send survives the user closing the tab mid-wizard.
 *
 * Delivery outcome is written back onto the same invite doc
 * (`inviteEmailSentAt`, `inviteEmailError`), so a caller that wants to surface
 * status can subscribe to it.
 *
 * Note: for a long time nothing consumed these fields, so this function wrote
 * the address and the invite silently went nowhere. If you're changing the
 * field names here, change the trigger's guard clauses to match.
 */
export const sendInviteEmail = async (familyId, email, inviteCode) => {
  const db = getDb();
  const normalized = String(email || '').trim().toLowerCase();
  await Promise.all([
    updateDoc(doc(db, 'families', familyId), {
      inviteEmail: normalized
    }),
    updateDoc(doc(db, 'invites', inviteCode), {
      invitedEmail: normalized
    })
  ]);
};

export const addHouseholdMember = async (familyId, { displayName, color, householdOf, isRegistered = false, userId = null }) => {
  const db = getDb();
  const memberId = isRegistered && userId ? userId : `hm_${Math.random().toString(36).substring(2, 10)}`;

  const memberData = {
    displayName,
    color: color || '#8b5cf6',
    householdOf,
    role: 'household_member',
    isRegistered,
    userId: isRegistered ? userId : null,
    addedBy: householdOf,
    addedAt: Timestamp.now()
  };

  await updateDoc(doc(db, 'families', familyId), {
    [`householdMembers.${memberId}`]: memberData
  });

  return memberId;
};

export const removeHouseholdMember = async (familyId, memberId) => {
  const db = getDb();
  const familyDoc = await getDoc(doc(db, 'families', familyId));
  if (!familyDoc.exists()) throw new Error('Family not found');

  const family = familyDoc.data();
  const member = family.householdMembers?.[memberId];
  if (!member) throw new Error('Household member not found');

  const updates = { [`householdMembers.${memberId}`]: deleteField() };
  const promises = [updateDoc(doc(db, 'families', familyId), updates)];

  if (member.isRegistered && member.userId) {
    promises.push(
      updateDoc(doc(db, 'users', member.userId), {
        familyIds: arrayRemove(familyId)
      })
    );
  }

  await Promise.all(promises);
};

export const updateHouseholdMemberField = async (familyId, memberId, field, value) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId), {
    [`householdMembers.${memberId}.${field}`]: value
  });
};

export const generateHouseholdInvite = async (familyId, householdOf) => {
  const db = getDb();
  const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

  await setDoc(doc(db, 'invites', inviteCode), {
    familyId,
    invitedBy: householdOf,
    invitedEmail: null,
    status: 'pending',
    type: 'household',
    householdOf,
    createdAt: Timestamp.now(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  });

  return inviteCode;
};

export const joinFamilyAsHouseholdMember = async (userId, userDisplayName, inviteCode, householdOf) => {
  const db = getDb();
  const inviteDocSnap = await getDoc(doc(db, 'invites', inviteCode.toUpperCase()));
  if (!inviteDocSnap.exists()) throw new Error('Invalid invite code');

  const invite = inviteDocSnap.data();
  if (invite.status === 'accepted') throw new Error('This invite has already been used');

  const familyId = invite.familyId;
  const familyDocSnap = await getDoc(doc(db, 'families', familyId));
  if (!familyDocSnap.exists()) throw new Error('Family not found');

  const family = familyDocSnap.data();

  if (family.parentIds.includes(userId)) {
    throw new Error('You are already a primary parent in this family');
  }
  if (family.householdMembers?.[userId]) {
    throw new Error('You are already a household member in this family');
  }

  const memberData = {
    displayName: userDisplayName,
    color: '#8b5cf6',
    householdOf: householdOf || invite.householdOf,
    role: 'household_member',
    isRegistered: true,
    userId,
    addedBy: invite.invitedBy,
    addedAt: Timestamp.now()
  };

  // Atomic so a partial failure can't burn the invite while leaving the user
  // out of the family (see joinFamily for the lockout this prevents).
  const batch = writeBatch(db);
  batch.update(doc(db, 'families', familyId), {
    [`householdMembers.${userId}`]: memberData
  });
  batch.update(doc(db, 'users', userId), {
    familyIds: arrayUnion(familyId),
    familyRole: 'household_member'
  });
  batch.update(doc(db, 'invites', inviteCode.toUpperCase()), { status: 'accepted' });
  await batch.commit();

  return familyId;
};

export const getInvite = async (inviteCode) => {
  const db = getDb();
  const snap = await getDoc(doc(db, 'invites', inviteCode.toUpperCase()));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const generateCoparentInvite = async (familyId, invitedBy) => {
  const db = getDb();
  const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

  // Atomic so the family's advertised `inviteCode` and its backing invite doc
  // can never diverge (see createFamily for the "Invalid invite code" failure
  // this prevents). Backs the "Generate New Invite Link" action in Settings.
  const batch = writeBatch(db);
  batch.set(doc(db, 'invites', inviteCode), {
    familyId,
    invitedBy,
    invitedEmail: null,
    status: 'pending',
    type: 'coparent',
    createdAt: Timestamp.now(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  });
  batch.update(doc(db, 'families', familyId), {
    inviteCode,
    inviteStatus: 'pending'
  });
  await batch.commit();

  return inviteCode;
};

export const generateMergeInvite = async (familyId, invitedBy, mergeTargetId, mergeTargetType) => {
  const db = getDb();
  const familyDoc = await getDoc(doc(db, 'families', familyId));
  if (!familyDoc.exists()) throw new Error('Family not found');

  const family = familyDoc.data();

  if (mergeTargetType === 'parent') {
    const profile = family.parentProfiles?.[mergeTargetId];
    if (!profile?.placeholder) throw new Error('Target is not a placeholder parent');
  } else if (mergeTargetType === 'household_member') {
    const member = family.householdMembers?.[mergeTargetId];
    if (!member) throw new Error('Household member not found');
    if (member.isRegistered) throw new Error('This member is already registered');
  } else {
    throw new Error('Invalid merge target type');
  }

  const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

  await setDoc(doc(db, 'invites', inviteCode), {
    familyId,
    invitedBy,
    invitedEmail: null,
    status: 'pending',
    type: 'merge',
    mergeTargetId,
    mergeTargetType,
    createdAt: Timestamp.now(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  });

  return inviteCode;
};

export const joinFamilyAsMergeTarget = async (userId, userDisplayName, inviteCode) => {
  const db = getDb();
  const inviteDocSnap = await getDoc(doc(db, 'invites', inviteCode.toUpperCase()));
  if (!inviteDocSnap.exists()) throw new Error('Invalid invite code');

  const invite = inviteDocSnap.data();
  if (invite.status === 'accepted') throw new Error('This invite has already been used');

  const { familyId, mergeTargetId, mergeTargetType } = invite;
  const familyDocSnap = await getDoc(doc(db, 'families', familyId));
  if (!familyDocSnap.exists()) throw new Error('Family not found');

  const family = familyDocSnap.data();

  if (family.parentIds.includes(userId) || family.householdMembers?.[userId]) {
    throw new Error('You are already a member of this family');
  }

  if (mergeTargetType === 'parent') {
    const profile = family.parentProfiles?.[mergeTargetId];
    if (!profile?.placeholder) throw new Error('This placeholder has already been replaced');

    const newParentIds = family.parentIds
      .filter(id => id !== mergeTargetId)
      .concat(userId);

    const newProfiles = { ...family.parentProfiles };
    delete newProfiles[mergeTargetId];
    newProfiles[userId] = {
      displayName: userDisplayName,
      color: profile.color || '#ec4899',
      role: 'parent'
    };

    const familyUpdate = {
      parentIds: newParentIds,
      parentProfiles: newProfiles,
      inviteStatus: 'accepted'
    };

    if (family.settings) {
      const newSettings = { ...family.settings };
      if (newSettings.parentLabels?.[mergeTargetId]) {
        newSettings.parentLabels = { ...newSettings.parentLabels };
        newSettings.parentLabels[userId] = newSettings.parentLabels[mergeTargetId];
        delete newSettings.parentLabels[mergeTargetId];
      }
      familyUpdate.settings = newSettings;
    }

    // Atomic — never burn the invite without completing the merge (see joinFamily).
    const batch = writeBatch(db);
    batch.update(doc(db, 'families', familyId), familyUpdate);
    batch.update(doc(db, 'users', userId), { familyIds: arrayUnion(familyId) });
    batch.update(doc(db, 'invites', inviteCode.toUpperCase()), { status: 'accepted' });
    await batch.commit();

    await migratePlaceholderOverrides(familyId, mergeTargetId, userId);

  } else if (mergeTargetType === 'household_member') {
    const member = family.householdMembers?.[mergeTargetId];
    if (!member) throw new Error('This member has been removed from the family');
    if (member.isRegistered) throw new Error('This member has already been registered');

    // Atomic — never burn the invite without completing the merge (see joinFamily).
    const batch = writeBatch(db);
    batch.update(doc(db, 'families', familyId), {
      [`householdMembers.${mergeTargetId}`]: deleteField(),
      [`householdMembers.${userId}`]: {
        ...member,
        displayName: userDisplayName,
        isRegistered: true,
        userId
      }
    });
    batch.update(doc(db, 'users', userId), {
      familyIds: arrayUnion(familyId),
      familyRole: 'household_member'
    });
    batch.update(doc(db, 'invites', inviteCode.toUpperCase()), { status: 'accepted' });
    await batch.commit();
  }

  return familyId;
};
